import React, { useEffect, useState } from "react";
import { Box, Typography, Grid, Paper, SxProps } from "@mui/material";
import { PieChart } from "@mui/x-charts/PieChart";
import { BarChart } from "@mui/x-charts/BarChart";
import { GridColDef } from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";
import {
  getDeadLineStatusStats,
  getMandatoryMessageGrids,
  getMandatoryMessageOverAllStats,
} from "../api/requests/dashboard";
import MessageRecipientsPopup from "./messageRecipientsPopup";
import { PaginationType } from "./Groups";
import UserAnalyticsTable from "./UserAnalyticsTable";
import { formatFullDateTime } from "../utils/utils";
import ActiveTickIcon from "../../src/assets/ActiveTickIcon.svg";
import { colors } from "../utils/constants";
import {
  titleDescriptionStyles,
  titleStyles,
} from "../components/CommDataGrid";
import { BASE } from "../routes/routePaths";
import { mainContentStyles } from "./Dashboard";

const StatCard = ({
  label,
  value,
  backgroundColor,
  color,
}: {
  label: string;
  value: string | number;
  backgroundColor?: string;
  color?: string;
}) => (
  <Paper
    elevation={2}
    sx={{ ...statCardPaperStyles, backgroundColor: backgroundColor || "#fff" }}
  >
    <Typography variant="subtitle2">{label}</Typography>
    <Typography fontWeight={600} variant="h5" sx={{ color: color }}>
      {value}
    </Typography>
  </Paper>
);

const MandatoryMessageDashboard: React.FC = () => {
  const [overallStatisticsData, setOverallStatisticsData] = useState({
    totalMessages: 0,
    totalRecipients: 0,
    totalSeen: 0,
    seenPercentage: 0,
  });
  const [deadLineStatisticsData, setDeadLineStatisticsData] = useState({
    overdueCount: 0,
    currentlyActiveCount: 0,
  });

  const [mandatoryMessageData, setMandatoryMessageData] = useState<any>(null);
  const [openPopup, setOpenPopup] = useState<boolean>(false);
  const [selectedRow, setSelectedRow] = useState<any>(null);
  const [paginationModel, setPaginationModel] = useState<PaginationType>({
    page: 0,
    pageSize: 5,
  });
  const [totalRowsCount, setTotalRowsCount] = useState<number>(0);
  //To do
  const [loading, setLoading] = useState<boolean>(false);

  const handleViewClick = (rowData: any) => {
    setSelectedRow(rowData);
    setOpenPopup(true);
  };

  const navigate = useNavigate();

  const getOverallStatistics = async () => {
    const response = await getMandatoryMessageOverAllStats();
    setOverallStatisticsData(response?.data);
    return response;
  };

  const getMandatoryMessagesData = async () => {
    setLoading(true);
    try {
      const response = await getMandatoryMessageGrids({
        page: paginationModel.page,
        size: paginationModel.pageSize,
      });
      if (response && response.messages) {
        const updatedResponse = response.messages.map((msg: any) => ({
          id: msg.messageId,
          sender: msg.senderName,
          message: msg.messageText || "(No message)",
          date: formatFullDateTime(msg.createdOn),
          deadline: formatFullDateTime(msg.deadline),
          status: msg.status,
          recipients: `${msg.recipientsCount} total`,
          seen: `${msg.seenPercentage}%`,
          originalData: msg,
        }));
        setMandatoryMessageData(updatedResponse);
        setTotalRowsCount(response.totalCount);
      } else {
        setMandatoryMessageData([]);
        setTotalRowsCount(0);
      }
    } catch (error) {
      console.error("Error fetching mandatory messages:", error);
    } finally {
      setLoading(false);
    }
  };

  const getDeadLineStatusData = async () => {
    const response = await getDeadLineStatusStats();
    setDeadLineStatisticsData(response?.data);
    return response;
  };

  const columns: GridColDef[] = [
    { field: "sender", headerName: "Sender", flex: 1 },
    { field: "message", headerName: "Message", flex: 1.5 },
    { field: "date", headerName: "Date", flex: 1 },
    //To do later
    // { field: 'deadline', headerName: 'Deadline', flex: 1 },
    {
      field: "status",
      headerName: "Status",
      flex: 0.6,
      renderCell: (params) => (
        <Box sx={statusStyles}>
          <img src={ActiveTickIcon} />
          <Typography fontSize={14}>{params.value}</Typography>
        </Box>
      ),
    },
    {
      field: "recipients",
      headerName: "Recipients",
      flex: 1,
      renderCell: (params) => (
        <Typography fontSize={14}>{params.value}</Typography>
      ),
    },
    {
      field: "seen",
      headerName: "Seen",
      flex: 0.8,
      renderCell: (params) => (
        <Typography fontSize={14}>{params.value} </Typography>
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      sortable: false,
      disableColumnMenu: true,
      width: 150,
      renderCell: (params: any) => (
        <Box
          sx={viewButtonStyles}
          onClick={() => handleViewClick(params.row.originalData)}
        >
          <Typography
            sx={{ fontWeight: 400, cursor: "pointer", fontSize: "14px" }}
          >
            View
          </Typography>
        </Box>
      ),
    },
  ];

  useEffect(() => {
    getOverallStatistics();
    getDeadLineStatusData();
  }, []);

  useEffect(() => {
    getMandatoryMessagesData();
  }, [paginationModel]);

  return (
    <>
      <Box sx={{ ...mainContentStyles, pl: 3 }}>
        <Box sx={breadcrumbMainStyles}>
          <Typography
            onClick={() => navigate(BASE)}
            sx={breadcrumbIntitalStyles}
          >
            Dashboard
          </Typography>
          <Typography sx={breadcrumbActiveStyles}>
            / Mandatory Message Dashboard
          </Typography>
        </Box>

        <Box sx={titleSectionStyles}>
          <Typography sx={titleStyles}>Mandatory Message Dashboard</Typography>
          <Typography sx={titleDescriptionStyles}>
            View mandatory messages statistics and recipient details.
          </Typography>
        </Box>
        <Box sx={mainScrollContent}>
          {/* Grid Layout */}
          <Box sx={mainGridStyles}>
            {/* Grid 1 */}
            <Box sx={gridStyles}>
              <Typography sx={overviewHeader} gutterBottom>
                Overall Statistics
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={4}>
                  <StatCard
                    label="Total Messages"
                    value={overallStatisticsData.totalMessages || 0}
                    backgroundColor="#e3f2fd"
                  />
                </Grid>
                <Grid item xs={4}>
                  <StatCard
                    label="Total Recipients"
                    value={overallStatisticsData.totalRecipients || 0}
                    backgroundColor="#e3f2fd"
                  />
                </Grid>
                <Grid item xs={4}>
                  <StatCard
                    label="Seen Percentage"
                    value={overallStatisticsData.seenPercentage || 0}
                    backgroundColor="#e3f2fd"
                  />
                </Grid>
              </Grid>

              <Box display="flex">
                <PieChart
                  series={[
                    {
                      data: [
                        {
                          id: 0,
                          value: overallStatisticsData.totalSeen,
                          label: "Seen",
                          color: "#4ade80",
                        },
                        {
                          id: 1,
                          value:
                            overallStatisticsData.totalRecipients -
                            overallStatisticsData.totalSeen,
                          label: "Unseen",
                          color: "#f87171	",
                        },
                      ],
                    },
                  ]}
                  width={300}
                  height={300}
                  slotProps={{
                    legend: {
                      position: {
                        vertical: "bottom",
                        horizontal: "middle",
                      },
                      direction: "row",
                      itemGap: 30,
                      itemMarkWidth: 12,
                      itemMarkHeight: 12,
                    },
                  }}
                />
              </Box>
            </Box>

            {/* Grid 2 */}
            <Box sx={gridStyles}>
              <Typography gutterBottom sx={overviewHeader}>
                Deadline Status
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <StatCard
                    label="Overdue"
                    value={deadLineStatisticsData.overdueCount || 0}
                    backgroundColor="#fef2f2"
                    color="#dc2626"
                  />
                </Grid>
                <Grid item xs={6}>
                  <StatCard
                    label="Active"
                    value={deadLineStatisticsData.currentlyActiveCount || 0}
                    backgroundColor="#f0fdf4"
                    color="#16a34a"
                  />
                </Grid>
              </Grid>

              <Box display="flex">
                <BarChart
                  xAxis={[{ scaleType: "band", data: ["Overdue", "Active"] }]}
                  series={[
                    {
                      data: [deadLineStatisticsData.overdueCount, 0],
                      color: "#f87171",
                    },
                    {
                      data: [0, deadLineStatisticsData.currentlyActiveCount],
                      color: "#4ade80",
                    },
                  ]}
                  width={400}
                  height={300}
                  slotProps={{
                    legend: {
                      position: { vertical: "bottom", horizontal: "middle" },
                      labelStyle: {
                        fontSize: 12,
                        fill: "#333",
                      },
                      itemGap: 10,
                    },
                  }}
                  sx={{
                    "& .MuiChartsLegend-root": {
                      marginTop: "20px",
                      background: "red",
                    },
                  }}
                />
              </Box>
            </Box>

            {/* Grid 3 */}
            <Box gridColumn="1 / span 2">
              <Box>
                <Typography fontSize={20} mb={2}>
                  Mandatory Messages
                </Typography>
                <div style={{ height: 400, width: "100%" }}>
                  <UserAnalyticsTable
                    rows={mandatoryMessageData}
                    columns={columns}
                    setPaginationModel={setPaginationModel}
                    paginationModel={paginationModel}
                    totalRowsCount={totalRowsCount}
                  />
                  {selectedRow && (
                    <MessageRecipientsPopup
                      open={openPopup}
                      onClose={() => setOpenPopup(false)}
                      data={selectedRow}
                    />
                  )}
                </div>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default MandatoryMessageDashboard;

const statCardPaperStyles: SxProps = {
  padding: 2,
  textAlign: "center",
  boxShadow: "none",
};

const statusStyles: SxProps = {
  borderRadius: "20px",
  display: "flex",
  flexDirection: "row",
  gap: 1,
};

export const breadcrumbIntitalStyles: SxProps = {
  cursor: "pointer",
  color: colors.spanishGrayColor,
  fontWeight: 500,
  fontSize: "14px",
};

export const breadcrumbActiveStyles: SxProps = {
  color: colors.indigoBlueColor,
  fontSize: "14px",
};

export const breadcrumbMainStyles: SxProps = {
  display: "flex",
  gap: 1,
  alignItems: "center",
  pt: 1,
  pb: 2,
};

export const viewButtonStyles: SxProps = {
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  gap: 1,
  height: "20px",
  padding: "6px",
  borderRadius: "4px",
  cursor: "pointer",
  color: colors.cobaltBlueColor,
};

export const overviewHeader: SxProps = {
  fontSize: "20px",
  fontWeight: 400,
  color: colors.jetColor,
  pb: 1,
};

export const mainGridStyles: SxProps = {
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gridTemplateRows: "auto auto",
  gap: 2,
};

export const gridStyles: SxProps = { backgroundColor: colors.white, p: 2 };

export const mainScrollContent: SxProps = {
  overflowY: "auto",
  height: "calc(100vh - 200px)",
};

export const titleSectionStyles: SxProps = {
  display: "flex",
  flexDirection: "column",
  mb: 2,
};
